// CHUNK 10: Synastry result page at /sinastria/:id
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ShareSynastryModal } from '../components/synastry/ShareSynastryModal';

interface SynastryRow {
  id:                  string;
  partner_name:        string;
  star_score:          number | null;
  compatibility_score: number | null;
  reading_text:        string | null;
  relation_type:       string | null;
  status:              string;
  metadata:            Record<string, string>;
  calculated_aspects:  unknown[];
  invite_link_token:   string | null;
  created_at:          string;
}

function formatText(text: string): string {
  return text
    .replace(/### (.*)/g,   '<h3 class="text-xl text-[#C9A84C] mt-8 mb-3 font-light tracking-wide">$1</h3>')
    .replace(/## (.*)/g,    '<h2 class="text-2xl text-[#C9A84C] mt-10 mb-4 font-light tracking-wide">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 font-semibold">$1</strong>')
    .replace(/⭐+/g,         (s) => `<span class="text-lg">${s}</span>`)
    .split('\n\n')
    .map(p => `<p class="mb-4 text-gray-700 leading-relaxed">${p}</p>`)
    .join('');
}

export function SynastryResult() {
  const { id }             = useParams<{ id: string }>();
  const [searchParams]     = useSearchParams();
  const navigate           = useNavigate();
  const [data, setData]    = useState<SynastryRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  const isPending = searchParams.get('pending') === '1';

  useEffect(() => {
    if (!id) return;
    supabase
      .from('synastry_readings')
      .select('*')
      .eq('id', id)
      .maybeSingle()
      .then(({ data: row, error }) => {
        if (error || !row) setNotFound(true);
        else setData(row as SynastryRow);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (data?.invite_link_token) {
      const base = window.location.origin;
      setInviteLink(`${base}/sinastria/convite/${data.invite_link_token}`);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f0f0] flex items-center justify-center">
        <span className="text-[#C9A84C] animate-pulse text-2xl">✦</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#f0f0f0] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-[#C9A84C] text-lg">Análise não encontrada</p>
          <button onClick={() => navigate('/sinastria/criar')} className="mt-4 text-gray-500 text-xs uppercase tracking-wider hover:text-gray-800">
            Criar nova sinastria
          </button>
        </div>
      </div>
    );
  }

  const meta       = data?.metadata ?? {};
  const personA    = meta.person_a_name ?? 'Você';
  const personB    = data?.partner_name ?? meta.person_b_name ?? '…';
  const stars      = data?.star_score ?? Math.round((data?.compatibility_score ?? 60) / 20);
  const starsText  = '⭐'.repeat(Math.min(5, Math.max(1, stars)));

  // Pending invite state
  if (isPending || data?.status === 'pending') {
    return (
      <div className="min-h-screen bg-[#f0f0f0] py-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="text-[#C9A84C] text-3xl tracking-[8px] mb-6">✦</div>
          <h1 className="text-[#C9A84C] text-2xl font-light mb-4">Aguardando resposta</h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Compartilhe o link abaixo. Quando a outra pessoa preencher os dados,
            a análise será gerada e vocês poderão ver aqui.
          </p>

          {inviteLink && (
            <div className="border border-[#C9A84C]/30 p-4 rounded-sm mb-6 bg-white">
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Link de convite</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={inviteLink}
                  className="flex-1 bg-gray-50 border border-gray-200 text-gray-700 text-xs p-2 rounded-sm"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(inviteLink); }}
                  className="px-3 py-2 bg-[#C9A84C] text-[#0D1130] text-xs font-bold hover:bg-[#C9A84C]/90"
                >
                  Copiar
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => { window.location.reload(); }}
            className="text-gray-400 text-xs uppercase tracking-wider hover:text-gray-800"
          >
            Verificar resultado ↺
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f0f0] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="text-[#C9A84C] text-2xl tracking-[8px] mb-4">✦</div>
          <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-2">
            Análise de Sinastria
          </h1>
          <p className="text-gray-800 text-xl font-light">{personA} × {personB}</p>
          <div className="mt-4 inline-flex items-center gap-2 border border-[#C9A84C]/30 px-5 py-2 rounded-sm bg-white">
            <span className="text-gray-500 text-xs">Compatibilidade:</span>
            <span className="text-lg">{starsText}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-3 mb-10">
          <button
            onClick={() => setShowShare(true)}
            className="px-6 py-3 border border-[#C9A84C] text-[#C9A84C] text-xs uppercase tracking-[2px] hover:bg-[#C9A84C]/10 transition-colors"
          >
            Compartilhar
          </button>
          <button
            onClick={() => navigate('/sinastria/criar')}
            className="px-6 py-3 border border-gray-300 text-gray-500 text-xs uppercase tracking-[2px] hover:text-gray-800 hover:border-gray-400 transition-colors"
          >
            Nova sinastria
          </button>
        </div>

        {/* Analysis content */}
        {data?.reading_text ? (
          <div className="border border-[#C9A84C]/20 rounded-sm p-8 md:p-12 bg-white">
            <div dangerouslySetInnerHTML={{ __html: formatText(data.reading_text) }} />
          </div>
        ) : (
          <div className="text-center text-gray-400 py-12">
            Análise indisponível
          </div>
        )}

        {/* Aspects accordion */}
        {(data?.calculated_aspects?.length ?? 0) > 0 && (
          <details className="mt-8 border border-[#C9A84C]/20 rounded-sm p-5 bg-white">
            <summary className="cursor-pointer text-gray-600 text-xs uppercase tracking-wider">
              Ver aspectos astrológicos detalhados
            </summary>
            <div className="mt-4 space-y-2">
              {(data!.calculated_aspects as Record<string, unknown>[]).map((a, i) => (
                <div key={i} className="border-l-2 border-[#C9A84C]/30 pl-4 py-1">
                  <div className="text-gray-700 text-sm">
                    {String(a.planetA)} {String(a.aspectPt)} {String(a.planetB)}
                  </div>
                  <div className="text-gray-400 text-xs">
                    Orbe: {String(a.orb)}° · Força: {String(a.strength)}/10
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* CTA */}
        <div className="mt-14 text-center border-t border-gray-300 pt-10">
          <p className="text-gray-500 text-sm mb-4">
            Descubra a compatibilidade com outras pessoas
          </p>
          <button
            onClick={() => navigate('/sinastria/criar')}
            className="px-8 py-4 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 transition-colors"
          >
            ✦ Criar nova análise ✦
          </button>
        </div>
      </div>

      {showShare && (
        <ShareSynastryModal
          synastryId={id ?? ''}
          personAName={personA}
          personBName={personB}
          starScore={stars}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}
